import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { apiRequest } from '../utils/api';
import type { User as UserType } from '../types';
import { formatDateLocal } from '../utils/dateUtils';
import DateView from './attendance/DateView';
import WeekView from './attendance/WeekView';
import MonthView from './attendance/MonthView';
import AttendanceDialog from './attendance/AttendanceDialog';

interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
}

interface AttendanceEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  checkin: string | null;
  checkout: string | null;
  in_position: string | null;
  out_position: string | null;
  task_hours?: number;
  total_hours?: number;
  log_type?: string;
}

export default function AttendanceSheet() {
  const [users, setUsers] = useState<User[]>([]);
  const [attendance, setAttendance] = useState<AttendanceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AttendanceEntry | null>(null);

  // Load current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as UserType);
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };

    loadCurrentUser();
  }, []);

  // Load all users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await apiRequest('/users', { method: 'GET' });
        const usersData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, []);

  // State to track date ranges from views
  const [dateRange, setDateRange] = useState<{ dateFrom: string; dateTo: string } | null>(null);

  // Calculate default date range based on current tab
  const getDefaultDateRange = () => {
    const today = new Date();
    let dateFrom: Date;
    let dateTo: Date;

    if (currentTab === 0) {
      // Month view: full calendar year (Jan 1 - Dec 31)
      dateFrom = new Date(today.getFullYear(), 0, 1);
      dateFrom.setHours(0, 0, 0, 0);

      dateTo = new Date(today.getFullYear(), 11, 31);
      dateTo.setHours(23, 59, 59, 999);
    } else if (currentTab === 1) {
      // Week view: 4 weeks from current week
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      // Calculate days to subtract to get to Sunday (week starts on Sunday)
      const diff = startOfWeek.getDate() - day;
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);

      dateFrom = new Date(startOfWeek);
      dateFrom.setDate(dateFrom.getDate() - 7); // One week before

      dateTo = new Date(startOfWeek);
      dateTo.setDate(dateTo.getDate() + (4 * 7) - 1); // 4 weeks ahead
      dateTo.setHours(23, 59, 59, 999);
    } else {
      // Date view: current week (exact dates, no buffer)
      const startOfWeek = new Date(today);
      const day = startOfWeek.getDay();
      // Calculate days to subtract to get to Sunday (week starts on Sunday)
      const diff = startOfWeek.getDate() - day;
      startOfWeek.setDate(diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      dateFrom = new Date(startOfWeek);
      
      dateTo = new Date(startOfWeek);
      dateTo.setDate(dateTo.getDate() + 6); // End of week (7 days total: 0-6)
      dateTo.setHours(23, 59, 59, 999);
    }

    return {
      dateFrom: formatDateLocal(dateFrom),
      dateTo: formatDateLocal(dateTo),
    };
  };

  // Load attendance data with date filtering
  const loadAttendance = async () => {
    setLoading(true);
    try {
      const range = dateRange || getDefaultDateRange();
      // Send date range to backend to filter by date field in database
      const response = await apiRequest(
        `/working-log/attendance?date_from=${range.dateFrom}&date_to=${range.dateTo}&limit=300`,
        { method: 'GET' }
      );
      const result = response.data;
      
      let data: any[] = [];
      if (Array.isArray(result)) {
        data = result;
      } else {
        data = result?.data || [];
      }

      const mapped: AttendanceEntry[] = data.map((entry: any) => ({
        id: entry.id,
        user_id: entry.user_id,
        user_name: entry.user_name || 'Unknown',
        user_email: entry.user_email || '',
        date: entry.date,
        checkin: entry.checkin,
        checkout: entry.checkout,
        in_position: entry.in_position,
        out_position: entry.out_position,
        task_hours: entry.task_hours || 0,
        total_hours: entry.total_hours,
        log_type: entry.log_type || 'working_log',
      }));

      setAttendance(mapped);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDateRange(null); // Reset date range when tab changes
  }, [currentTab]);

  // Load attendance when tab changes or date range changes
  useEffect(() => {
    loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, dateRange]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const canManage = currentUser?.role === 'SYSTEM ADMIN';

  const handleCreateAttendance = (user?: User, date?: Date) => {
    if (user && date) {
      // Create a new entry object for the specific user/date
      const newEntry: AttendanceEntry = {
        id: 0, // Temporary ID for new entries
        user_id: user.id,
        user_name: user.username,
        user_email: user.email,
        date: formatDateLocal(date),
        checkin: null,
        checkout: null,
        in_position: null,
        out_position: null,
      };
      setEditingEntry(newEntry);
    } else {
      setEditingEntry(null);
    }
    setOpenDialog(true);
  };

  const handleEditAttendance = (entry: AttendanceEntry) => {
    setEditingEntry(entry);
    setOpenDialog(true);
  };

  const handleDeleteAttendance = async (id: number, logType?: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      // If it's a task work log, use the task work log endpoint
      if (logType === 'task_work_log') {
        await apiRequest(`/working-log/task-work-logs/${id}`, { method: 'DELETE' });
      } else {
        // Otherwise, try the attendance endpoint
        await apiRequest(`/working-log/attendance/${id}`, { method: 'DELETE' });
      }
      // Reload all attendance data
      await loadAttendance();
    } catch (error: any) {
      console.error('Error deleting record:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete record';
      alert(errorMessage);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingEntry(null);
  };

  const handleDialogSave = async (data: {
    user_id: number;
    date: string;
    checkin: string | null;
    checkout: string | null;
    in_position?: string | null;
    out_position?: string | null;
    record_id?: number | null;
  }) => {
    try {
      // Use record_id from dialog if provided, otherwise fall back to editingEntry.id
      const recordId = data.record_id ?? (editingEntry && editingEntry.id > 0 ? editingEntry.id : null);
      
      if (recordId && recordId > 0) {
        // Update existing record
        await apiRequest(`/working-log/attendance/${recordId}`, {
          method: 'PUT',
          body: JSON.stringify({
            checkin: data.checkin,
            checkout: data.checkout,
            in_position: data.in_position,
            out_position: data.out_position,
          }),
        });
      } else {
        // Create new record
        await apiRequest('/working-log/attendance', {
          method: 'POST',
          body: JSON.stringify({
            user_id: data.user_id,
            date: data.date,
            checkin: data.checkin,
            checkout: data.checkout,
            in_position: data.in_position,
            out_position: data.out_position,
          }),
        });
      }

      // Reload all attendance data
      await loadAttendance();

      handleDialogClose();
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Failed to save attendance record');
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Attendance Management
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
            Attendance Sheet
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleCreateAttendance()}
            sx={{ mt: 1 }}
          >
            Add Attendance
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="attendance view tabs">
          <Tab label="Month" />
          <Tab label="Week" />
          <Tab label="Date" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <MonthView
          users={users}
          attendance={attendance}
          loading={loading}
          canManage={canManage}
          onEdit={handleEditAttendance}
          onDelete={handleDeleteAttendance}
          onDateRangeChange={setDateRange}
        />
      )}
      {currentTab === 1 && (
        <WeekView
          users={users}
          attendance={attendance}
          loading={loading}
          canManage={canManage}
          onEdit={handleEditAttendance}
          onDelete={handleDeleteAttendance}
          onDateRangeChange={setDateRange}
        />
      )}
      {currentTab === 2 && (
        <DateView
          users={users}
          attendance={attendance}
          loading={loading}
          canManage={canManage}
          onEdit={handleEditAttendance}
          onDelete={handleDeleteAttendance}
          onCreate={handleCreateAttendance}
          onDateRangeChange={setDateRange}
        />
      )}

      {/* Attendance Dialog */}
      <AttendanceDialog
        open={openDialog}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        users={users}
        entry={editingEntry}
      />
    </Box>
  );
}
